import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import type {FC} from 'react'
import {useCallback, useEffect, useState} from 'react'
import {ToastProvider} from 'react-toast-notifications'
import CategoryContent from './components/CategoryContent'
import CategoryLandscape from './components/CategoryLandscape'
import CategorySwitcher from './components/CategorySwitcher'
import DownloadCta from './components/DownloadCta'
import FastestMovers from './components/FastestMovers'
import HeroSection from './components/HeroSection'
import ReportFooter from './components/ReportFooter'
import {CATEGORIES} from './data/categories'
import type {CategoryKey} from './data/types'
import styles from './spendBenchmarks.module.css'

export type SpendBenchmarksProps = SliceComponentProps<Content.SpendBenchmarksSlice>

const SpendBenchmarks: FC<SpendBenchmarksProps> = ({slice}) => {
  const [currentCategory, setCurrentCategory] = useState<CategoryKey>('ai-tools')

  // Read category from URL hash on mount for deep linking
  useEffect(() => {
    const hash = (window.location.hash || '').match(/cat=([\w-]+)/)

    if (hash && CATEGORIES[hash[1] as CategoryKey]) {
      setCurrentCategory(hash[1] as CategoryKey)
    }
  }, [])

  const goToCategory = useCallback((key: CategoryKey) => {
    setCurrentCategory(key)

    if (typeof window !== 'undefined' && window.history.replaceState) {
      window.history.replaceState(null, '', '#cat=' + key)
    }

    const hero = document.querySelector('.hero')

    if (hero) {
      const topnavH = 60

      const target = Math.max(
        0,
        hero.getBoundingClientRect().height + hero.getBoundingClientRect().top + window.scrollY - topnavH,
      )

      window.scrollTo({top: target, behavior: 'auto'})
    }
  }, [])

  const pdfUrl = isFilled.link(slice.primary.pdf_report) ? slice.primary.pdf_report.url : null
  const hubspotFormId = isFilled.keyText(slice.primary.hubspot_form_id) ? slice.primary.hubspot_form_id : null
  const showDownload = !!pdfUrl && !!hubspotFormId

  return (
    <ToastProvider autoDismiss={5000}>
      <section data-slice-type={slice.slice_type} data-slice-variation={slice.variation}>
        <div className={styles.reportRoot}>
          <HeroSection hasPdf={showDownload} />
          <CategorySwitcher currentCategory={currentCategory} onCategoryChange={goToCategory} />
          <CategoryContent categoryKey={currentCategory} />
          <CategoryLandscape />
          <FastestMovers />
          {showDownload && <DownloadCta pdfUrl={pdfUrl} hubspotFormId={hubspotFormId} />}
          <ReportFooter />
        </div>
      </section>
    </ToastProvider>
  )
}

export default SpendBenchmarks
